import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { MetricCard } from '../components/ui/metric-card';
import { Checkbox } from '../components/ui/checkbox';
import { useColumnConfig, ColumnConfigPopover } from '../components/ColumnConfig';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Area,
  AreaChart,
} from 'recharts';
import { apiFetch } from '../utils/api';
import ReviewTicker from '../components/ReviewTicker';
import { useAuth } from '../context/AuthContext';
import {
  Star,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  AlertTriangle,
  Heart,
  Store,
  Trophy,
  AlertCircle,
  Activity,
  Package,
  ArrowLeft,
  Search,
  Clock,
  Brain,
  ChevronRight,
  Lightbulb,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Globe,
  Info,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  Tooltip as ShadTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ['#2eb4ac', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];
const RATING_COLORS = { 1: '#ef4444', 2: '#f97316', 3: '#f59e0b', 4: '#84cc16', 5: '#2eb4ac' };

// Expandable text component
const ExpandableText = ({ text, maxLength = 150 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  if (text.length <= maxLength) return <span>{text}</span>;
  
  return (
    <span>
      {expanded ? text : `${text.slice(0, maxLength)}...`}
      <button 
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        className="ml-1 text-[#2eb4ac] hover:underline text-xs font-medium inline-flex items-center"
      >
        {expanded ? <><ChevronUp className="w-3 h-3" /> Less</> : <><ChevronDown className="w-3 h-3" /> More</>}
      </button>
    </span>
  );
};

export default function BrandDashboardPage() {
  const { brandName } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Real-time rating states
  const [realtimeData, setRealtimeData] = useState([]);
  const [fetchingRealtime, setFetchingRealtime] = useState(false);
  const [scrapeMessage, setScrapeMessage] = useState(null);
  const [brandLogo, setBrandLogo] = useState(null);
  const [platformIcons, setPlatformIcons] = useState({});
  const [lastFetched, setLastFetched] = useState(null);
  const [autoFetchInterval, setAutoFetchInterval] = useState(0);

  const PLATFORM_ORDER = ['Amazon', 'Flipkart', 'Zepto', 'Blinkit'];

  const defaultBrandSkuCols = useMemo(() => [
    { id: 'product', label: 'Product', visible: true },
    { id: 'amazon_rating', label: 'Amazon', visible: true },
    { id: 'flipkart_rating', label: 'Flipkart', visible: true },
    { id: 'zepto_rating', label: 'Zepto', visible: true },
    { id: 'blinkit_rating', label: 'Blinkit', visible: true },
    { id: 'reviews', label: 'Reviews', visible: false },
    { id: 'rating', label: 'Analysed Rating', visible: false },
    { id: 'sentiment', label: 'Sentiment', visible: false },
  ], []);
  const { columns: brandSkuCols, setColumns: setBrandSkuCols, visibleColumns: visBrandSkuCols } = useColumnConfig('brand_sku_columns_v2', defaultBrandSkuCols);

  useEffect(() => {
    if (brandName) {
      fetchBrandData();
      fetchRealtimeData();
      fetchBrandMeta();
    }
  }, [brandName]);

  const fetchBrandMeta = async () => {
    try {
      const [brandsRes, platformsRes] = await Promise.all([
        apiFetch('/api/brands', { credentials: 'include' }),
        apiFetch('/api/platforms', { credentials: 'include' }),
      ]);
      if (brandsRes.ok) {
        const bl = await brandsRes.json();
        const match = bl.find(b => b.name === brandName);
        if (match?.logo_url) setBrandLogo(match.logo_url);
      }
      if (platformsRes.ok) {
        const pl = await platformsRes.json();
        const icons = {};
        pl.forEach(p => { if (p.icon_url) icons[p.name] = p.icon_url; });
        setPlatformIcons(icons);
      }
    } catch (err) { console.error(err); }
  };

  const [fetchingLiveRatings, setFetchingLiveRatings] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  // Hidden-products state (per-brand, per-user preference)
  const [hiddenSkus, setHiddenSkus] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [hideConfirmArmed, setHideConfirmArmed] = useState(false);
  const [unhideConfirmArmed, setUnhideConfirmArmed] = useState(false);

  const hiddenKey = `hidden_skus_${brandName}`;

  // Load hidden SKUs preference on brand change (GLOBAL, admin-managed)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/admin/hidden-skus/${encodeURIComponent(brandName)}`);
        if (res.ok && !cancelled) {
          const json = await res.json();
          setHiddenSkus(Array.isArray(json?.hidden) ? json.hidden : []);
        }
      } catch (err) { console.error('Load hidden SKUs failed', err); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandName]);

  const persistHidden = async (next) => {
    setHiddenSkus(next);
    try {
      const res = await apiFetch(`/api/admin/hidden-skus/${encodeURIComponent(brandName)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden: next }),
      });
      if (!res.ok && res.status === 403) {
        // shouldn't happen since UI is hidden for non-admins, but defensive
        console.warn('Only admins can update hidden SKUs');
      }
    } catch (err) { console.error('Persist hidden SKUs failed', err); }
  };

  const toggleSelectSku = (productName) => {
    setSelectedSkus(prev =>
      prev.includes(productName) ? prev.filter(n => n !== productName) : [...prev, productName]
    );
  };

  const hideSelected = async () => {
    if (!hideConfirmArmed) {
      setHideConfirmArmed(true);
      setTimeout(() => setHideConfirmArmed(false), 4000);
      return;
    }
    const merged = Array.from(new Set([...hiddenSkus, ...selectedSkus]));
    await persistHidden(merged);
    setSelectedSkus([]);
    setHideConfirmArmed(false);
  };

  const unhideSelected = async () => {
    if (!unhideConfirmArmed) {
      setUnhideConfirmArmed(true);
      setTimeout(() => setUnhideConfirmArmed(false), 4000);
      return;
    }
    const next = hiddenSkus.filter(n => !selectedSkus.includes(n));
    await persistHidden(next);
    setSelectedSkus([]);
    setUnhideConfirmArmed(false);
  };

  const fetchAllBrandLiveRatings = async () => {
    setFetchingLiveRatings(true);
    try {
      await apiFetch(`/api/scraper/sku-fetch-all-brand?brand=${encodeURIComponent(brandName)}`, { method: 'POST' });
      setLastFetchedAt(new Date().toISOString());
      await fetchBrandData();
    } catch (err) { console.error(err); }
    setFetchingLiveRatings(false);
  };

  const fetchBrandData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/dashboard/brand/${encodeURIComponent(brandName)}`);
      if (response.ok) {
        setData(await response.json());
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to load brand data');
      }
    } catch (err) {
      console.error('Error fetching brand data:', err);
      setError('Failed to load brand data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtimeData = async () => {
    try {
      const response = await apiFetch(`/api/scraper/brand-summary/${encodeURIComponent(brandName)}`);
      if (response.ok) {
        const result = await response.json();
        setRealtimeData(result.platforms || []);
        setLastFetched(result.last_fetched || result.last_global_fetch || null);
        setAutoFetchInterval(result.auto_fetch_interval || 0);
      }
    } catch (err) {
      console.error('Error fetching realtime data:', err);
    }
  };

  const refreshRealtimeRatings = async () => {
    setFetchingRealtime(true);
    setScrapeMessage(null);
    try {
      const response = await apiFetch(`/api/scraper/fetch-all?brand=${encodeURIComponent(brandName)}`, {
        method: 'POST',
      });
      if (response.ok) {
        const result = await response.json();
        if (result.message) {
          setScrapeMessage(result.message);
        }
        await fetchRealtimeData();
      }
    } catch (err) {
      console.error('Error refreshing realtime:', err);
      setScrapeMessage('Failed to connect to scraping service');
    } finally {
      setFetchingRealtime(false);
    }
  };

  // Navigate to reviews with filter
  const navigateToReviews = (filters) => {
    const params = new URLSearchParams();
    if (filters.keyword) params.set('keyword', filters.keyword);
    if (filters.topic) params.set('topic', filters.topic);
    if (filters.highlight) params.set('highlight', filters.highlight);
    if (brandName) params.set('brand', brandName);
    if (filters.sentiment) params.set('sentiment', filters.sentiment);
    navigate(`/reviews?${params.toString()}`);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm mb-2">{label}</p>
          {payload.map((entry) => (
            <p key={entry.name} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate metrics
  const positiveCount = data?.sentiment_distribution?.positive || 0;
  const negativeCount = data?.sentiment_distribution?.negative || 0;
  const neutralCount = data?.sentiment_distribution?.neutral || 0;
  const totalSentiment = positiveCount + negativeCount + neutralCount;
  const positivePercent = totalSentiment > 0 ? Math.round((positiveCount / totalSentiment) * 100) : 0;
  const negativePercent = totalSentiment > 0 ? Math.round((negativeCount / totalSentiment) * 100) : 0;

  // Unified channel ratings: prefer live scraped data, fallback to uploaded review averages
  const channelRatings = realtimeData.length > 0
    ? realtimeData.map(p => ({ platform: p.platform, avg_rating: p.avg_rating, total_reviews: p.total_reviews, total_text_reviews: p.total_text_reviews, isLive: true }))
    : (data?.platform_performance || []).map(p => ({ platform: p.platform, avg_rating: p.avg_rating, total_reviews: p.total_reviews, total_text_reviews: 0, isLive: false }));

  // Ranking: Channel Rating desc → Reviews desc → SKU Rating desc
  const maxChannelRating = channelRatings.length > 0
    ? Math.max(...channelRatings.filter(ch => ch.avg_rating).map(ch => ch.avg_rating), 0)
    : 0;
  const sortedSkus = [...(data?.sku_performance || [])].sort((a, b) => {
    // Primary: channel rating (same for all SKUs at brand level, included for correctness)
    // Secondary: number of reviews descending
    if (b.total_reviews !== a.total_reviews) return b.total_reviews - a.total_reviews;
    // Tertiary: SKU-level rating descending
    return (b.avg_rating || 0) - (a.avg_rating || 0);
  });
  // Apply hide filter based on user pref + Show Hidden toggle
  const visibleSkus = showHidden
    ? sortedSkus.filter(s => hiddenSkus.includes(s.product_name))
    : sortedSkus.filter(s => !hiddenSkus.includes(s.product_name));
  const rankedTopSkus = sortedSkus.filter(s => !hiddenSkus.includes(s.product_name) && s.total_reviews >= 3).slice(0, 5);
  const rankedUnderperformingSkus = [...sortedSkus.filter(s => !hiddenSkus.includes(s.product_name) && s.total_reviews >= 2)].reverse().slice(0, 5);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <AlertCircle className="w-16 h-16 text-destructive" />
        <h2 className="text-xl font-semibold">{error}</h2>
        <Button onClick={() => navigate('/dashboard')} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="brand-dashboard-page">
      {/* Header with Breadcrumb */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <span className="cursor-pointer hover:text-foreground" onClick={() => navigate('/dashboard')}>Dashboard</span>
            <span>/</span>
            <span>{data?.brand_name || brandName}</span>
          </div>
          <h1 className="text-3xl font-bold font-heading tracking-tight flex items-center gap-2">
            {brandLogo ? (
              <img src={brandLogo} alt="" className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <Store className="w-8 h-8 text-[#2eb4ac]" />
            )}
            {data?.brand_name}
          </h1>
          <p className="text-muted-foreground mt-1">Brand Level Dashboard</p>
        </div>
      </div>

      {/* Review history ticker — scoped to this brand */}
      <ReviewTicker brand={data?.brand_name || brandName} />

      {/* Platform Summary - aggregated from per-SKU live data */}
      <Card className="card-hover border-[#8b5cf6]/20 border-t-2 border-t-[#8b5cf6]">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#8b5cf6]" />
              <CardTitle className="text-base">Platform Summary</CardTitle>
              <Badge className="bg-[#8b5cf6]/15 text-[#8b5cf6] border-[#8b5cf6]/30 text-[9px] px-1.5 py-0 font-semibold" variant="outline">BETA</Badge>
              <TooltipProvider>
                <ShadTooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px] text-xs">
                    Weighted average ratings computed from per-SKU live data tracked in the Control Panel. Accuracy depends on the number of SKUs tracked per platform.
                  </TooltipContent>
                </ShadTooltip>
              </TooltipProvider>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="gap-1.5 h-7 text-xs text-muted-foreground hover:text-[#8b5cf6]" data-testid="manage-platforms-btn">
              Manage in Control Panel
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {(() => {
            // Aggregate per-platform stats from sku_performance live_ratings
            const platformMap = {};
            (data?.sku_performance || []).forEach((sku) => {
              (sku.live_ratings || []).forEach((lr) => {
                if (!platformMap[lr.platform]) {
                  platformMap[lr.platform] = { totalWeight: 0, totalCount: 0, skuCount: 0, ratings: [] };
                }
                const count = lr.review_count || 0;
                const rating = lr.rating || 0;
                platformMap[lr.platform].totalWeight += rating * count;
                platformMap[lr.platform].totalCount += count;
                platformMap[lr.platform].skuCount += 1;
                if (rating) platformMap[lr.platform].ratings.push(rating);
              });
            });
            (data?.platform_performance || []).forEach((pp) => {
              if (!platformMap[pp.platform]) {
                platformMap[pp.platform] = { totalWeight: 0, totalCount: 0, skuCount: 0, ratings: [] };
              }
            });

            const platforms = Object.entries(platformMap).map(([name, d]) => {
              // Amazon: weighted average (per-SKU ratings differ)
              // Others: actual rating (brand-level, same across SKUs)
              const isAmazon = name.toLowerCase() === 'amazon';
              let avg_rating = null;
              if (isAmazon && d.totalCount > 0) {
                avg_rating = (d.totalWeight / d.totalCount).toFixed(1);
              } else if (d.ratings.length > 0) {
                avg_rating = d.ratings[0];
              }
              return {
                platform: name,
                avg_rating,
                total_ratings: d.totalCount,
                sku_count: d.skuCount,
                is_weighted: isAmazon,
              };
            });

            // Always surface "Customer Support" as a channel — it's not URL-tracked,
            // so it wouldn't otherwise appear in this grid.
            if (!platforms.some(p => p.platform === 'Customer Support')) {
              platforms.push({
                platform: 'Customer Support',
                avg_rating: null,
                total_ratings: null,
                sku_count: null,
                is_call_logs: true,
              });
            }

            if (platforms.length === 0) {
              return (
                <div className="text-center py-8 text-muted-foreground">
                  <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="mb-2">No product URLs tracked yet</p>
                  <p className="text-sm">Go to <button onClick={() => navigate('/settings')} className="text-[#8b5cf6] hover:underline font-medium">Control Panel</button> to add tracked SKUs</p>
                </div>
              );
            }

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {platforms.map((p) => (
                  <div
                    key={p.platform}
                    onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(p.platform)}`)}
                    className="p-5 rounded-xl border border-border bg-gradient-to-br from-background to-accent/10 hover:border-[#8b5cf6]/50 hover:scale-[1.02] transition-all cursor-pointer group"
                    data-testid={`platform-summary-${p.platform.toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        {platformIcons[p.platform] ? (
                          <img src={platformIcons[p.platform]} alt="" className="w-5 h-5 rounded object-cover" />
                        ) : (
                          <Store className="w-5 h-5 text-[#8b5cf6]" />
                        )}
                        <span className="font-semibold text-lg">{p.platform}</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <Star className="w-6 h-6 text-[#f59e0b] fill-[#f59e0b]" />
                        <span className="text-3xl font-bold">{p.avg_rating || '—'}</span>
                        <span className="text-sm text-muted-foreground mt-1">/5</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {p.total_ratings > 0 ? `${p.total_ratings.toLocaleString()} ratings` : 'No ratings yet'}
                        <span className="block text-xs mt-0.5">{p.sku_count} SKU{p.sku_count !== 1 ? 's' : ''} tracked{p.is_weighted ? ' (weighted avg)' : ''}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </CardContent>

      {/* Row 1: KPIs with Info Icons - No Health Score */}
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard
          metricKey="total_reviews"
          icon={MessageSquare}
          label="Analysed Reviews"
          value={data?.total_reviews?.toLocaleString()}
          subtext="All platforms"
        />
        <MetricCard
          metricKey="total_skus"
          icon={Package}
          label="Analysed SKUs"
          value={data?.total_skus}
          subtext="Products"
        />
        <MetricCard
          metricKey="positive"
          icon={TrendingUp}
          iconColor="text-[#2eb4ac]"
          label="Positive"
          value={positivePercent}
          suffix="%"
          subtext={`${positiveCount} reviews`}
          onClick={() => navigateToReviews({ sentiment: 'positive' })}
          highlight={true}
          highlightColor="#2eb4ac"
        />
        <MetricCard
          metricKey="negative"
          icon={TrendingDown}
          iconColor="text-destructive"
          label="Negative"
          value={negativePercent}
          suffix="%"
          subtext={`${negativeCount} reviews`}
          onClick={() => navigateToReviews({ sentiment: 'negative' })}
        />
      </div>

      {/* AI Summary - 1-2 liner review summary */}
      {data?.ai_summary && (
        <Card className="card-hover bg-gradient-to-r from-[#2eb4ac]/5 via-[#3b82f6]/5 to-transparent border-l-4 border-l-[#2eb4ac]" data-testid="ai-summary">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#2eb4ac]/10 flex items-center justify-center flex-shrink-0">
                <Brain className="w-5 h-5 text-[#2eb4ac]" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-[#2eb4ac] mb-1 uppercase tracking-wide">AI Insight</p>
                <p className="text-sm text-foreground">
                  {data?.total_reviews > 0 
                    ? `${data?.brand_name} has ${data?.total_reviews?.toLocaleString()} reviews across ${data?.platform_performance?.length || 0} channels with ${positivePercent}% positive sentiment. Top praise: "${data?.top_topics?.[0]?.topic || 'quality'}". Main concern: "${data?.detected_issues?.[0]?.issue || 'delivery'}" (${data?.detected_issues?.[0]?.count || 0} mentions).`
                    : 'Upload reviews to get AI-powered brand insights.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Row 2: SKU Performance Table */}
      <Card className="card-hover" data-testid="sku-performance-table">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Package className="w-4 h-4 text-[#3b82f6]" />
                SKU Performance
                {isAdmin && hiddenSkus.length > 0 && !showHidden && (
                  <Badge variant="secondary" className="text-[10px]" data-testid="hidden-count-badge">
                    {hiddenSkus.length} hidden
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {showHidden ? 'Hidden products — select & unhide to bring back' : 'Click any row to view product dashboard'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Bulk action bar — admin only */}
              {isAdmin && selectedSkus.length > 0 && (
                <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-red-500/5 border border-red-500/20" data-testid="bulk-hide-bar">
                  <span className="text-xs text-muted-foreground">{selectedSkus.length} selected</span>
                  {showHidden ? (
                    <button
                      onClick={unhideSelected}
                      className={`text-xs font-medium px-2 py-1 rounded-md transition-colors ${unhideConfirmArmed ? 'bg-[#2eb4ac] text-white' : 'text-[#2eb4ac] hover:bg-[#2eb4ac]/10'}`}
                      data-testid="bulk-unhide-btn"
                    >
                      <Eye className="w-3 h-3 inline mr-1" />
                      {unhideConfirmArmed ? `Click again to unhide ${selectedSkus.length}` : `Unhide ${selectedSkus.length}`}
                    </button>
                  ) : (
                    <button
                      onClick={hideSelected}
                      className={`text-xs font-medium px-2 py-1 rounded-md transition-colors ${hideConfirmArmed ? 'bg-red-500 text-white' : 'text-red-500 hover:bg-red-500/10'}`}
                      data-testid="bulk-hide-btn"
                    >
                      <EyeOff className="w-3 h-3 inline mr-1" />
                      {hideConfirmArmed ? `Click again to hide ${selectedSkus.length}` : `Hide ${selectedSkus.length}`}
                    </button>
                  )}
                  <button
                    onClick={() => { setSelectedSkus([]); setHideConfirmArmed(false); setUnhideConfirmArmed(false); }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                    data-testid="bulk-clear-btn"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {isAdmin && hiddenSkus.length > 0 && (
                <button
                  onClick={() => { setShowHidden(!showHidden); setSelectedSkus([]); }}
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  data-testid="toggle-hidden-btn"
                >
                  {showHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {showHidden ? 'Show active' : `Show hidden (${hiddenSkus.length})`}
                </button>
              )}
              {lastFetchedAt && (
                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(lastFetchedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <ColumnConfigPopover columns={brandSkuCols} onChange={setBrandSkuCols} />
              <button
                onClick={fetchAllBrandLiveRatings}
                disabled={fetchingLiveRatings}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2eb4ac]/10 text-[#2eb4ac] text-xs font-medium hover:bg-[#2eb4ac]/20 disabled:opacity-50 transition-all"
                data-testid="fetch-brand-live-ratings-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${fetchingLiveRatings ? 'animate-spin' : ''}`} />
                {fetchingLiveRatings ? 'Fetching...' : 'Fetch Live Ratings'}
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="[&>div]:max-h-[500px] [&>div]:overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && (
                    <TableHead className="sticky top-0 z-10 bg-card w-10">
                      <Checkbox
                        checked={visibleSkus.length > 0 && selectedSkus.length === visibleSkus.length}
                        onCheckedChange={(v) => {
                          if (v) setSelectedSkus(visibleSkus.map(s => s.product_name));
                          else setSelectedSkus([]);
                        }}
                        aria-label="Select all"
                        data-testid="select-all-skus-checkbox"
                      />
                    </TableHead>
                  )}
                  {visBrandSkuCols.map(col => (
                    <TableHead key={col.id} className={`sticky top-0 z-10 bg-card ${col.id === 'product' ? '' : 'text-center'}`}>
                      {['Amazon', 'Flipkart', 'Zepto', 'Blinkit'].includes(col.label) ? (
                        <div className="flex items-center justify-center gap-1">
                          {platformIcons[col.label] ? <img src={platformIcons[col.label]} alt="" className="w-4 h-4 rounded object-cover" /> : <Store className="w-3 h-3" />}
                          {col.label}
                        </div>
                      ) : col.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleSkus.map((sku) => {
                  const platMap = {};
                  (sku.live_ratings || []).forEach(lr => { platMap[lr.platform] = lr; });
                  const isSelected = selectedSkus.includes(sku.product_name);
                  return (
                  <TableRow
                    key={sku.product_name}
                    className={`cursor-pointer hover:bg-accent/50 group ${isSelected ? 'bg-accent/30' : ''}`}
                    onClick={() => {
                      const firstPlatform = data?.platform_performance?.[0]?.platform || 'Amazon';
                      navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(firstPlatform)}/product/${encodeURIComponent(sku.product_name)}`);
                    }}
                  >
                    {isAdmin && (
                      <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelectSku(sku.product_name)}
                          aria-label={`Select ${sku.product_name}`}
                          data-testid={`select-sku-${sku.product_name}`}
                        />
                      </TableCell>
                    )}
                    {visBrandSkuCols.map(col => {
                      if (col.id === 'product') return (
                        <TableCell key={col.id} className="font-medium max-w-[250px]">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded bg-accent/50 shrink-0 overflow-hidden flex items-center justify-center">
                              {sku.image_url ? <img src={sku.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : <Package className="w-3 h-3 text-muted-foreground" />}
                            </div>
                            <span className="truncate text-[#2eb4ac] hover:underline">{sku.product_name}</span>
                            <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                          </div>
                        </TableCell>
                      );
                      if (col.id.endsWith('_rating') && col.id !== 'rating') {
                        const platName = col.label;
                        const lr = platMap[platName];
                        return (
                          <TableCell key={col.id} className="text-center">
                            {lr ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b] text-xs">
                                  <Star className="w-3 h-3 mr-0.5 fill-current" />{lr.rating}
                                </Badge>
                                {lr.review_count != null && (
                                  <span className="text-[10px] text-muted-foreground">{lr.review_count.toLocaleString()}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-[10px] text-muted-foreground/60 italic" data-testid={`not-live-yet-${sku.product_name}-${platName}`}>Not Live Yet</span>
                            )}
                          </TableCell>
                        );
                      }
                      if (col.id === 'reviews') return (
                        <TableCell key={col.id} className="text-center">{sku.total_reviews || null}</TableCell>
                      );
                      if (col.id === 'rating') return (
                        <TableCell key={col.id} className="text-center">
                          {sku.avg_rating > 0 ? (
                            <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b]">
                              <Star className="w-3 h-3 mr-1 fill-current" />{sku.avg_rating}
                            </Badge>
                          ) : (
                            <span className="text-[10px] text-muted-foreground/60 italic" data-testid={`not-live-yet-rating-${sku.product_name}`}>Not Live Yet</span>
                          )}
                        </TableCell>
                      );
                      if (col.id === 'sentiment') return (
                        <TableCell key={col.id} className="text-center">
                          {sku.total_reviews > 0 ? (
                            <div className="flex items-center justify-center gap-3">
                              <span className="text-xs text-red-400 font-medium">{sku.negative_count}<span className="text-muted-foreground/60 ml-0.5">neg</span></span>
                              <span className="text-xs text-slate-400 font-medium">{sku.total_reviews - sku.positive_count - sku.negative_count}<span className="text-muted-foreground/60 ml-0.5">neu</span></span>
                            </div>
                          ) : null}
                        </TableCell>
                      );
                      return null;
                    })}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>        </CardContent>
      </Card>

      {/* Row 4: Top & Underperforming SKUs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing SKUs */}
        <Card className="card-hover" data-testid="top-skus">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#f59e0b]" />
              🏆 Top Performing SKUs
            </CardTitle>
            <CardDescription>Click to view product dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rankedTopSkus.map((sku, idx) => (
                <button
                  key={sku.product_name}
                  onClick={() => {
                    const firstPlatform = data?.platform_performance?.[0]?.platform || 'Amazon';
                    navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(firstPlatform)}/product/${encodeURIComponent(sku.product_name)}`);
                  }}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-accent/30 hover:bg-accent/50 transition-all cursor-pointer group text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xl">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '📦'}</span>
                    <div className="w-7 h-7 rounded bg-accent/50 shrink-0 overflow-hidden flex items-center justify-center">
                      {sku.image_url ? <img src={sku.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : <Package className="w-3 h-3 text-muted-foreground" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate max-w-[180px]">{sku.product_name}</p>
                      <p className="text-xs text-muted-foreground">{sku.total_reviews} reviews</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sku.avg_rating > 0 ? (
                      <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b]">
                        <Star className="w-3 h-3 mr-1 fill-current" />{sku.avg_rating}
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/60 italic" data-testid={`not-live-yet-top-${sku.product_name}`}>Not Live Yet</span>
                    )}
                    <Badge variant="secondary" className="bg-[#2eb4ac]/10 text-[#2eb4ac]">{sku.sentiment_score}%</Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
              {!data?.top_skus?.length && <p className="text-sm text-muted-foreground text-center py-4">No data available</p>}
            </div>
          </CardContent>
        </Card>

        {/* Underperforming SKUs - with sentiment % */}
        <Card className="card-hover" data-testid="underperforming-skus">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-destructive" />
              Underperforming SKUs
            </CardTitle>
            <CardDescription>Click to view product dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rankedUnderperformingSkus.map((sku) => (
                <button
                  key={sku.product_name}
                  onClick={() => {
                    const firstPlatform = data?.platform_performance?.[0]?.platform || 'Amazon';
                    navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(firstPlatform)}/product/${encodeURIComponent(sku.product_name)}`);
                  }}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-destructive/5 hover:bg-destructive/10 transition-all cursor-pointer group text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                    <div className="w-7 h-7 rounded bg-accent/50 shrink-0 overflow-hidden flex items-center justify-center">
                      {sku.image_url ? <img src={sku.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : <Package className="w-3 h-3 text-muted-foreground" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate max-w-[180px]">{sku.product_name}</p>
                      <p className="text-xs text-muted-foreground">{sku.total_reviews} reviews</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sku.avg_rating > 0 ? (
                      <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b]">
                        <Star className="w-3 h-3 mr-1 fill-current" />{sku.avg_rating}
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/60 italic" data-testid={`not-live-yet-under-${sku.product_name}`}>Not Live Yet</span>
                    )}
                    <Badge variant="secondary" className={`${sku.sentiment_score >= 40 ? 'bg-[#f59e0b]/10 text-[#f59e0b]' : 'bg-destructive/10 text-destructive'}`}>
                      {sku.sentiment_score || 0}%
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
              {!data?.underperforming_skus?.length && <p className="text-sm text-muted-foreground text-center py-4">No underperforming SKUs</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 5: Topics - Clickable */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Positive Topics */}
        <Card className="card-hover" data-testid="positive-topics">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Heart className="w-4 h-4 text-[#2eb4ac]" />
              ❤️ Top Positive Topics
            </CardTitle>
            <CardDescription>Click to see reviews for each topic</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data?.positive_topics?.length > 0 ? (
                data.positive_topics.map((item) => (
                  <button
                    key={item.topic}
                    onClick={() => navigateToReviews({ topic: item.topic, sentiment: 'positive' })}
                    className="px-3 py-1.5 rounded-full bg-[#2eb4ac]/10 text-[#2eb4ac] text-sm font-medium hover:bg-[#2eb4ac]/20 hover:scale-105 transition-all cursor-pointer"
                  >
                    {item.topic} <span className="opacity-60">({item.count})</span>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No positive topics found</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Negative Topics */}
        <Card className="card-hover" data-testid="negative-topics">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              ⚠️ Top Negative Topics
            </CardTitle>
            <CardDescription>Click to see reviews for each topic</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data?.negative_topics?.length > 0 ? (
                data.negative_topics.map((item) => (
                  <button
                    key={item.topic}
                    onClick={() => navigateToReviews({ topic: item.topic, sentiment: 'negative' })}
                    className="px-3 py-1.5 rounded-full bg-destructive/10 text-destructive text-sm font-medium hover:bg-destructive/20 hover:scale-105 transition-all cursor-pointer"
                  >
                    {item.topic} <span className="opacity-60">({item.count})</span>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No negative topics found</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 6: Issues & Emerging Complaints - Clickable */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Issues */}
        <Card className="card-hover" data-testid="top-issues">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#f97316]" />
              🔥 Top Issues
            </CardTitle>
            <CardDescription>Click to see reviews mentioning each issue</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data?.top_issues?.length > 0 ? (
                data.top_issues.map((item) => (
                  <button
                    key={item.issue}
                    onClick={() => navigateToReviews({ highlight: item.issue })}
                    className="w-full flex items-center justify-between p-2 rounded-lg bg-[#f97316]/5 hover:bg-[#f97316]/10 transition-all cursor-pointer text-left"
                  >
                    <span className="text-sm font-medium capitalize">{item.issue}</span>
                    <Badge variant="secondary" className="bg-[#f97316]/10 text-[#f97316]">{item.count}</Badge>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No issues found</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Emerging Complaints */}
        <Card className="card-hover" data-testid="emerging-complaints">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Search className="w-4 h-4 text-[#8b5cf6]" />
              🔍 Emerging Complaint Trends
            </CardTitle>
            <CardDescription>Issues increasing recently</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.emerging_complaints?.length > 0 ? (
                data.emerging_complaints.map((item) => (
                  <button
                    key={item.issue}
                    onClick={() => navigateToReviews({ highlight: item.issue, sentiment: 'negative' })}
                    className="w-full flex items-center justify-between p-2 rounded-lg bg-accent/30 hover:bg-accent/50 transition-all cursor-pointer text-left"
                  >
                    <span className="text-sm font-medium capitalize">{item.issue}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{item.recent} recent</Badge>
                      <span className={`text-xs font-semibold ${item.change > 0 ? 'text-destructive' : 'text-[#2eb4ac]'}`}>
                        {item.change > 0 ? `↑${item.change}` : item.change === 0 ? '→' : `↓${Math.abs(item.change)}`}
                      </span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No emerging trends detected</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 7: Recent Critical Reviews */}
      <Card className="card-hover" data-testid="critical-reviews">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Clock className="w-4 h-4 text-destructive" />
            📝 Recent Critical Reviews (1★-2★)
          </CardTitle>
          <CardDescription>Latest reviews needing attention</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[250px]">
            <div className="space-y-3 pr-2">
              {data?.critical_reviews?.length > 0 ? (
                data.critical_reviews.map((review) => (
                  <div key={review.review_id} className="p-3 rounded-lg bg-destructive/5 border-l-4 border-destructive">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {platformIcons[review.platform] ? (
                            <img src={platformIcons[review.platform]} alt="" className="w-3 h-3 rounded object-cover mr-1" />
                          ) : null}
                          {review.platform}
                        </Badge>
                        <div className="flex">
                          {Array(5).fill(0).map((_, i) => (
                            <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'text-[#f59e0b] fill-[#f59e0b]' : 'text-muted'}`} />
                          ))}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground max-w-[150px] truncate">{review.product_name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <ExpandableText text={review.review_text} maxLength={150} />
                    </p>
                    {review.detected_issues?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {review.detected_issues.slice(0, 3).map((issue) => (
                          <button
                            key={issue}
                            onClick={() => navigateToReviews({ highlight: issue })}
                            className="px-2 py-0.5 rounded text-xs bg-destructive/10 text-destructive hover:bg-destructive/20 cursor-pointer"
                          >
                            {issue}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No critical reviews found - Great job! 🎉</p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
